import Phaser from 'phaser';
import { CANVAS_WIDTH, CANVAS_HEIGHT, TILE_SIZE } from '@/config/GameConfig';
import { LEVEL_TREE, LevelNode, Branch, isDemoCapped, DEMO_LEVEL_CAP } from '@/map/LevelTree';
import { ProgressionManager } from '@/systems/ProgressionManager';
import { generateMap } from '@/map/ProceduralMapGenerator';
import { setActiveMap } from '@/map/MapData';
import { startingResources } from '@/config/DifficultyScaling';
import { SoundManager } from '@/systems/SoundManager';
import { isDevMode, DEV_MODE_CHANGED } from '@/systems/DevConsole';
import { EventBus, GameEvents } from '@/events/EventBus';
import { t } from '@/i18n/I18n';
import { TileType } from '@/config/GameConfig';
import { TECH, drawTechRect, fillTechRect, createTechButton, createSmallTechButton, drawMetalPlate, createEngravedText, addTilt3DEffect, addShineEffect } from '@/ui/TechUI';
import { CampaignOverrideStore } from '@/systems/CampaignOverrideStore';
import { ResourceManager } from '@/systems/ResourceManager';
import { getSequenceIds, getSequence } from '@/cinematic/CinematicDirector';
import { SceneCinematicWatcher } from '@/cinematic/SceneCinematicWatcher';

// ── Layout constants ──
const TREE_X = 400;           // tree occupies right side
const TREE_W = 760;           // tree drawing width
const NODE_SPACING_Y = 150;   // vertical gap between tiers
const NODE_RECT_W = 150;      // rectangular node width
const NODE_RECT_H = 52;       // rectangular node height
const TOTAL_TIERS = 33;
const TREE_HEIGHT = TOTAL_TIERS * NODE_SPACING_Y + 300;

// Branch colors
const BRANCH_COLORS: Record<string, number> = {
  alpha: 0xcc4444,
  beta: 0x4488cc,
  gamma: 0x44cc44,
  merge: 0xccaa22,
};

// ── Medal tiers (best → worst) ──
export enum MedalTier {
  PLATINUM = 'Platinum',
  GOLD = 'Gold',
  SILVER = 'Silver',
  BRONZE = 'Bronze',
  IRON = 'Iron',
}

const MEDAL_COLORS: Record<MedalTier, number> = {
  [MedalTier.PLATINUM]: 0xd0e8ff,
  [MedalTier.GOLD]:     0xffcc00,
  [MedalTier.SILVER]:   0xcccccc,
  [MedalTier.BRONZE]:   0xcc8844,
  [MedalTier.IRON]:     0x777788,
};

const MEDAL_LABELS: Record<MedalTier, string> = {
  [MedalTier.PLATINUM]: '♛',
  [MedalTier.GOLD]:     '★',
  [MedalTier.SILVER]:   '★',
  [MedalTier.BRONZE]:   '★',
  [MedalTier.IRON]:     '✦',
};

const DIFF_COLORS: Record<string, number> = {
  Beginner: 0x66dd66,
  Easy: 0x44cc44,
  Normal: 0xaacc44,
  Moderate: 0xccaa22,
  Challenging: 0xddaa00,
  Hard: 0xcc6622,
  'Very Hard': 0xcc4444,
  Expert: 0xaa22cc,
  Nightmare: 0xff2222,
  Infernal: 0x880000,
};

export class LevelTreeScene extends Phaser.Scene {
  private treeCam!: Phaser.Cameras.Scene2D.Camera;
  private scrollY = 0;
  private nodeContainers: Map<string, Phaser.GameObjects.Container> = new Map();
  private selectedNode: LevelNode | null = null;
  private previewContainer!: Phaser.GameObjects.Container;
  private infoPanel!: Phaser.GameObjects.Container;
  private currencyText!: Phaser.GameObjects.Text;
  private coinIcon!: Phaser.GameObjects.Image;
  private resourceText!: Phaser.GameObjects.Text;
  private resCoIcon!: Phaser.GameObjects.Image;
  private resElectroIcon!: Phaser.GameObjects.Image;
  private electroCountText?: Phaser.GameObjects.Text;
  private progressContainer!: Phaser.GameObjects.Container;
  private actionBtn!: Phaser.GameObjects.Container;
  private actionLabel!: Phaser.GameObjects.Text;
  private editMapBtn!: Phaser.GameObjects.Container;
  private cineEditorBtn!: Phaser.GameObjects.Container;
  private selectionGlow!: Phaser.GameObjects.Graphics;
  private cinematicWatcher: SceneCinematicWatcher | null = null;


  constructor() {
    super({ key: 'LevelTreeScene' });
  }

  create(data?: { selectNodeId?: string }): void {
    this.nodeContainers.clear();
    this.selectedNode = null;
    // Start scrolled to bottom where tier 0 (starting node) is
    this.scrollY = TREE_HEIGHT - CANVAS_HEIGHT;

    this.cameras.main.setBackgroundColor('#0a0a18');
    // Main camera: left panel only (info, preview, buttons)
    this.cameras.main.setViewport(0, 0, TREE_X, CANVAS_HEIGHT);
    SoundManager.playRandomMapSelectMusic();

    // ── Left panel: info + preview (fixed, default camera) ──
    this.buildInfoPanel();

    // Currency + resources display (top-left, all on one line — must fit within TREE_X)
    const icoSz = 28;
    const topY = 18;
    const txtY = 8;
    const fontSize = '13px';
    // Coin icon + amount
    this.coinIcon = this.add.image(18, topY, 'icon_coin').setDisplaySize(icoSz, icoSz).setDepth(200);
    this.currencyText = this.add.text(34, txtY, '', {
      fontSize, color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
    }).setDepth(200);
    // Co icon + amount (positioned dynamically after currency)
    this.resCoIcon = this.add.image(0, topY, 'icon_co').setDisplaySize(icoSz, icoSz).setDepth(200);
    this.resourceText = this.add.text(0, txtY, '', {
      fontSize, color: '#44ee88', fontFamily: 'monospace', fontStyle: 'bold',
    }).setDepth(200);
    // Electro icon + amount (positioned dynamically after Co)
    this.resElectroIcon = this.add.image(0, topY, 'icon_electro').setDisplaySize(icoSz, icoSz).setDepth(200);
    this.electroCountText = this.add.text(0, txtY, '', {
      fontSize, color: '#88ccff', fontFamily: 'monospace', fontStyle: 'bold',
    }).setDepth(200);
    this.updateCurrencyDisplay();
    EventBus.on(GameEvents.CURRENCY_CHANGED, this.updateCurrencyDisplay, this);
    EventBus.on(GameEvents.RESOURCES_UPDATED, this.updateCurrencyDisplay, this);
    this.events.once('shutdown', () => {
      EventBus.off(GameEvents.CURRENCY_CHANGED, this.updateCurrencyDisplay, this);
      EventBus.off(GameEvents.RESOURCES_UPDATED, this.updateCurrencyDisplay, this);
    });

    // Campaign progression display
    this.buildProgressDisplay();

    // Back button (bottom-left)
    const backBtn = createSmallTechButton(this, 16 + 40, CANVAS_HEIGHT - 35 + 8, t('levelTree.back'), 0xaa8888, () => {
      SoundManager.play('click');
      this.scene.start('TitleScene');
    });
    backBtn.setDepth(200);

    // ESC → back to title
    this.input.keyboard?.on('keydown-ESC', () => {
      SoundManager.play('click');
      this.scene.start('TitleScene');
    });

    // ── Right panel: scrollable tree ──
    // Use a second camera for the tree area that we can scroll
    this.treeCam = this.cameras.add(TREE_X, 0, TREE_W, CANVAS_HEIGHT);
    this.treeCam.setBackgroundColor('#0d0d1a');
    this.treeCam.setBounds(0, 0, TREE_W, TREE_HEIGHT);
    this.treeCam.setScroll(0, this.scrollY);

    // treeCam must ignore all left-panel objects so they don't render in the tree area
    this.treeCam.ignore([
      this.previewContainer, this.infoPanel, this.actionBtn, this.editMapBtn, this.cineEditorBtn,
      this.coinIcon, this.currencyText, this.resourceText, this.resCoIcon, this.resElectroIcon, this.electroCountText!, this.progressContainer, backBtn,
    ]);

    // Draw connection lines first (behind nodes)
    this.drawConnections();

    // Draw nodes
    for (const node of LEVEL_TREE) {
      this.createNodeVisual(node);
    }

    // Demo limit line — visual separator between playable and locked levels
    if (!isDevMode()) {
      const capNode = LEVEL_TREE.find(n => n.index === DEMO_LEVEL_CAP);
      const firstLocked = LEVEL_TREE.find(n => n.index === DEMO_LEVEL_CAP + 1);
      if (capNode && firstLocked) {
        const capPos = this.nodeScreenPos(capNode);
        const lockPos = this.nodeScreenPos(firstLocked);
        const lineY = (capPos.y + lockPos.y) / 2;
        const lineGfx = this.add.graphics().setDepth(50);
        lineGfx.lineStyle(2, 0xff6644, 0.6);
        lineGfx.lineBetween(20, lineY, TREE_W - 20, lineY);
        // "DEMO" label
        const demoLabel = this.add.text(TREE_W / 2, lineY - 12, 'DEMO VERSION — Full game coming soon!', {
          fontSize: '11px', color: '#ff8866', fontFamily: 'monospace', fontStyle: 'bold',
          backgroundColor: '#111122', padding: { x: 8, y: 2 },
        }).setOrigin(0.5).setDepth(51);
        this.cameras.main.ignore([lineGfx, demoLabel]);
      }
    }

    // Selection glow (rendered above nodes in treeCam)
    this.selectionGlow = this.add.graphics().setDepth(100);
    this.selectionGlow.setVisible(false);
    this.cameras.main.ignore(this.selectionGlow);
    // Pulsing glow animation
    this.tweens.add({
      targets: this.selectionGlow,
      alpha: { from: 0.6, to: 1 },
      duration: 800,
      yoyo: true, repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Title (in tree area, fixed on screen)
    const title = this.add.text(TREE_W / 2, 15, t('levelTree.campaign'), {
      fontSize: '22px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5, 0).setDepth(200).setScrollFactor(0);
    this.cameras.main.ignore(title);

    // Scroll: mouse wheel
    // Phaser wheel callback: (pointer, gameObjects, deltaX, deltaY, deltaZ)
    this.input.on('wheel', (_p: Phaser.Input.Pointer, _go: Phaser.GameObjects.GameObject[], _dx: number, dy: number) => {
      this.scrollY = Phaser.Math.Clamp(
        this.scrollY + dy * 0.5,
        0, TREE_HEIGHT - CANVAS_HEIGHT,
      );
      this.treeCam.setScroll(0, this.scrollY);
    });

    // Select node: prefer the one passed via restart data, fallback to first
    const targetId = data?.selectNodeId;
    const targetNode = targetId ? LEVEL_TREE.find(n => n.id === targetId) : null;
    if (targetNode) {
      this.selectNode(targetNode);
      // Scroll tree camera to center on the selected node
      const pos = this.nodeScreenPos(targetNode);
      this.scrollY = Phaser.Math.Clamp(
        pos.y - CANVAS_HEIGHT / 2, 0, TREE_HEIGHT - CANVAS_HEIGHT,
      );
      this.treeCam.setScroll(0, this.scrollY);
    } else if (LEVEL_TREE.length > 0) {
      this.selectNode(LEVEL_TREE[0]);
    }

    // Cinematic trigger watcher — fires sequences targeting LevelTreeScene
    this.cinematicWatcher = new SceneCinematicWatcher(this, 'LevelTreeScene');
    this.cinematicWatcher.fire('levelTreeOpen');
    this.events.once('shutdown', () => {
      this.cinematicWatcher?.destroy();
      this.cinematicWatcher = null;
    });
  }

  // ── Info panel (left side, fixed camera) ──

  private buildInfoPanel(): void {
    // Preview container
    this.previewContainer = this.add.container(15, 60).setDepth(50);

    // Info panel below preview
    this.infoPanel = this.add.container(15, 340).setDepth(50);

    // Action button
    this.actionBtn = this.add.container(190, CANVAS_HEIGHT - 80).setDepth(200);
    const actionGfx = this.add.graphics();
    this.actionBtn.add(actionGfx);

    this.actionLabel = this.add.text(0, 0, '', {
      fontSize: '15px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5);
    this.actionBtn.add(this.actionLabel);

    const actionHit = this.add.rectangle(0, 0, 200, 40, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    this.actionBtn.add(actionHit);

    // Store gfx ref for redraw
    (this as any)._actionGfx = actionGfx;

    const drawActionBtn = (color: number) => {
      actionGfx.clear();
      fillTechRect(actionGfx, -100, -20, 200, 40, color, 0.3);
      drawTechRect(actionGfx, -100, -20, 200, 40, color, 0.8, 1.5);
    };
    drawActionBtn(TECH.SUCCESS);

    actionHit.on('pointerover', () => {
      drawActionBtn(TECH.HOVER);
      this.actionLabel.setColor('#ffffff');
    });
    actionHit.on('pointerout', () => {
      const isUnlock = this.actionLabel.text.includes('UNLOCK');
      drawActionBtn(isUnlock ? 0x335588 : TECH.SUCCESS);
    });
    actionHit.on('pointerdown', () => this.onAction());

    // ── Dev-only: EDIT MAP button ──
    this.editMapBtn = this.add.container(190, CANVAS_HEIGHT - 35).setDepth(200).setVisible(false);
    const editGfx = this.add.graphics();
    fillTechRect(editGfx, -60, -14, 120, 28, 0x886633, 0.3);
    drawTechRect(editGfx, -60, -14, 120, 28, 0x886633, 0.8, 1.5);
    this.editMapBtn.add(editGfx);
    const editLabel = this.add.text(0, 0, t('levelTree.editMap'), {
      fontSize: '11px', color: '#ffcc66', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5);
    this.editMapBtn.add(editLabel);
    const editHit = this.add.rectangle(0, 0, 120, 28, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    this.editMapBtn.add(editHit);
    editHit.on('pointerdown', () => this.onEditMap());

    // ── Dev-only: CINE EDITOR button ──
    this.cineEditorBtn = this.add.container(320, CANVAS_HEIGHT - 35).setDepth(200).setVisible(false);
    const cineGfx = this.add.graphics();
    fillTechRect(cineGfx, -50, -14, 100, 28, 0x3366aa, 0.3);
    drawTechRect(cineGfx, -50, -14, 100, 28, 0x3366aa, 0.8, 1.5);
    this.cineEditorBtn.add(cineGfx);
    const cineLabel = this.add.text(0, 0, 'CINE', {
      fontSize: '11px', color: '#44aaff', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5);
    this.cineEditorBtn.add(cineLabel);
    const cineHit = this.add.rectangle(0, 0, 100, 28, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    this.cineEditorBtn.add(cineHit);
    cineHit.on('pointerdown', () => {
      SoundManager.play('click');
      if (this.scene.isActive('CinematicEditorScene')) return;
      const callerScenes = [this.scene.key];
      for (const key of callerScenes) this.scene.pause(key);
      const initData: Record<string, unknown> = { hostSceneKey: this.scene.key, callerScenes };
      const ids = getSequenceIds();
      if (ids.length > 0) initData.sequence = getSequence(ids[0]);
      this.scene.launch('CinematicEditorScene', initData);
    });
  }

  // ── Tree node visuals ──

  private nodeScreenPos(node: LevelNode): { x: number; y: number } {
    // x: 0-1 mapped to tree width with padding
    const pad = 80;
    const x = pad + node.treeX * (TREE_W - pad * 2);
    // Inverted Y: tier 0 at bottom, highest tier at top
    const y = TREE_HEIGHT - 120 - node.tier * NODE_SPACING_Y + (node.treeYOffset ?? 0);
    return { x, y };
  }

  private createNodeVisual(node: LevelNode): void {
    const { x, y } = this.nodeScreenPos(node);
    const container = this.add.container(x, y);

    const unlocked = ProgressionManager.isUnlocked(node.id);
    const completed = ProgressionManager.isCompleted(node.id);
    const canUnlock = ProgressionManager.canUnlock(node.id);
    const demoCapped = isDemoCapped(node) && !isDevMode();

    const branchColor = BRANCH_COLORS[node.branch] ?? 0x888888;
    // Demo-capped = very dim; deeply locked = dimmed but still readable
    const alpha = demoCapped ? 0.2 : (unlocked ? 1 : (canUnlock ? 0.7 : 0.35));

    // Metallic plate background — uniform color for all nodes
    const plateGfx = this.add.graphics();
    const plateAlpha = unlocked ? 1 : (canUnlock ? 0.7 : 0.35);
    drawMetalPlate(plateGfx, -NODE_RECT_W / 2, -NODE_RECT_H / 2, NODE_RECT_W, NODE_RECT_H,
      0x556677, { alpha: plateAlpha });
    // Gold border for completed
    if (completed) {
      plateGfx.lineStyle(2, 0xffcc00, 0.9);
      plateGfx.strokeRect(-NODE_RECT_W / 2, -NODE_RECT_H / 2, NODE_RECT_W, NODE_RECT_H);
    }
    container.add(plateGfx);

    // Async shine sweep across campaign nodes (random offset per node)
    if (unlocked) {
      const randomDelay = 500 + Math.random() * 3500;
      addShineEffect(this, container, NODE_RECT_W, NODE_RECT_H, 4000, randomDelay);
    }

    // Hit area for interactions (invisible rect on top)
    const rect = this.add.rectangle(0, 0, NODE_RECT_W, NODE_RECT_H, 0x000000, 0);
    container.add(rect);

    // Node name — engraved text (shifted left if medal present)
    const displayName = node.name.length > 16 ? node.name.slice(0, 15) + '…' : node.name;
    const textOffsetX = completed ? -10 : 0;
    const nameEngraved = createEngravedText(this, textOffsetX, -8, displayName, {
      fontSize: '12px',
      color: completed ? '#ffcc00' : (unlocked ? '#ddeeff' : '#556677'),
    });
    container.add(nameEngraved);

    // Level number (below name) — engraved
    const numEngraved = createEngravedText(this, textOffsetX, 10, `#${node.index}`, {
      fontSize: '10px', color: '#8899aa', fontStyle: 'normal',
    });
    container.add(numEngraved);

    // Medal for completed nodes — same opaque style as mission-complete debrief
    if (completed) {
      const medal = this.getMedalTier(node);
      const mColor = MEDAL_COLORS[medal];
      const mColorStr = '#' + mColor.toString(16).padStart(6, '0');
      // Random rotation ±15° (seeded by node index for consistency)
      const seed = ((node.index * 7 + 13) % 31) / 31;
      const medalRotation = (seed - 0.5) * (Math.PI / 6);

      const mR = 18; // medal radius
      const medalContainer = this.add.container(NODE_RECT_W / 2 - 20, 0);
      medalContainer.setRotation(medalRotation);

      // Medal colors
      const mr = (mColor >> 16) & 0xff;
      const mg = (mColor >> 8) & 0xff;
      const mb = mColor & 0xff;
      const darkerMedal = ((Math.max(0, mr - 100) << 16) | (Math.max(0, mg - 100) << 8) | Math.max(0, mb - 100));
      const lighterMedal = ((Math.min(255, mr + 60) << 16) | (Math.min(255, mg + 60) << 8) | Math.min(255, mb + 60));

      const medalGfx = this.add.graphics();

      // Drop shadow
      medalGfx.fillStyle(0x000000, 0.35);
      medalGfx.fillCircle(2, 3, mR);

      // Base disc (dark)
      medalGfx.fillStyle(darkerMedal, 1);
      medalGfx.fillCircle(0, 0, mR);

      // Radial gradient feel: brighter center layer
      medalGfx.fillStyle(mColor, 0.45);
      medalGfx.fillCircle(-1, -1, mR - 3);
      medalGfx.fillStyle(lighterMedal, 0.2);
      medalGfx.fillCircle(-2, -3, mR * 0.55);

      // Outer rim (thick)
      medalGfx.lineStyle(2.5, mColor, 1);
      medalGfx.strokeCircle(0, 0, mR);

      // Inner bevel ring
      medalGfx.lineStyle(1, lighterMedal, 0.3);
      medalGfx.strokeCircle(0, 0, mR - 3);

      // Top-left highlight arc (light catch)
      medalGfx.lineStyle(1.5, 0xffffff, 0.35);
      medalGfx.beginPath();
      medalGfx.arc(0, 0, mR - 4, -Math.PI * 0.85, -Math.PI * 0.25);
      medalGfx.strokePath();

      // Bottom-right shadow arc
      medalGfx.lineStyle(1, 0x000000, 0.2);
      medalGfx.beginPath();
      medalGfx.arc(0, 0, mR - 4, Math.PI * 0.15, Math.PI * 0.75);
      medalGfx.strokePath();

      medalContainer.add(medalGfx);

      // Tier label centered on medal
      const tierText = this.add.text(0, 0, medal.toUpperCase(), {
        fontSize: '8px', color: mColorStr,
        fontFamily: 'monospace', fontStyle: 'bold',
        stroke: '#000000', strokeThickness: 2,
      }).setOrigin(0.5);
      medalContainer.add(tierText);

      container.add(medalContainer);
    }

    // Lock icon for deeply locked levels
    if (!unlocked && !canUnlock) {
      const lock = this.add.image(NODE_RECT_W / 2 - 6, -NODE_RECT_H / 2 - 4, 'icon_blocked')
        .setOrigin(0.5).setDisplaySize(50, 50);
      container.add(lock);
    }

    // Cost badge for unlockable nodes
    if (canUnlock) {
      const costBg = this.add.rectangle(0, NODE_RECT_H / 2 + 12, 64, 16, 0x333366, 0.9);
      costBg.setStrokeStyle(1, 0x6688cc);
      container.add(costBg);
      const costText = this.add.text(0, NODE_RECT_H / 2 + 12, `$${node.unlockCost}`, {
        fontSize: '10px', color: '#ffcc00', fontFamily: 'monospace',
      }).setOrigin(0.5);
      container.add(costText);
    }

    // Difficulty badge above rectangle
    const diffColor = DIFF_COLORS[node.difficulty] ?? 0xaaaaaa;
    const diffBg = this.add.rectangle(0, -NODE_RECT_H / 2 - 12, 72, 14, diffColor, 0.3);
    diffBg.setStrokeStyle(1, diffColor);
    container.add(diffBg);
    const diffText = this.add.text(0, -NODE_RECT_H / 2 - 12, node.difficulty, {
      fontSize: '9px', color: '#ffffff', fontFamily: 'monospace',
    }).setOrigin(0.5);
    container.add(diffText);

    // Interaction: only unlocked or unlockable nodes are interactive
    if (unlocked || canUnlock) {
      rect.setInteractive({ useHandCursor: true });
      rect.on('pointerdown', () => {
        SoundManager.play('clickSelect');
        this.selectNode(node);
      });

      // 3D tilt effect on hover (uses treeCam for correct coordinates)
      addTilt3DEffect(this, container, rect, NODE_RECT_W, NODE_RECT_H, {
        camera: this.treeCam,
        hoverScale: 1.08,
        tiltStrength: 0.07,
        shiftPx: 4,
      });

      // Subtle idle floating animation (staggered by node index)
      this.tweens.add({
        targets: container,
        y: y + 2,
        duration: 1800 + (node.index % 5) * 200,
        yoyo: true, repeat: -1,
        ease: 'Sine.easeInOut',
        delay: (node.index % 7) * 150,
      });
    }

    // Assign to tree camera only (not default)
    this.cameras.main.ignore(container);

    this.nodeContainers.set(node.id, container);
  }

  // ── Connection lines ──

  private drawConnections(): void {
    const gfxGlow = this.add.graphics().setDepth(-2);  // glow layer behind
    const gfx = this.add.graphics().setDepth(-1);       // main line layer
    this.cameras.main.ignore(gfxGlow);
    this.cameras.main.ignore(gfx);

    const halfH = NODE_RECT_H / 2;

    for (const node of LEVEL_TREE) {
      const to = this.nodeScreenPos(node);
      for (const prereqId of node.prereqs) {
        const prereq = LEVEL_TREE.find(n => n.id === prereqId);
        if (!prereq) continue;
        const from = this.nodeScreenPos(prereq);

        const completed = ProgressionManager.isCompleted(prereqId);
        const unlocked = ProgressionManager.isUnlocked(prereqId);
        const branchColor = BRANCH_COLORS[prereq.branch] ?? 0x888888;

        // Line colors: completed = gold, unlocked = branch color, locked = muted
        const lineColor = completed ? 0xccaa44 : (unlocked ? branchColor : 0x334466);
        const lineAlpha = completed ? 0.9 : (unlocked ? 0.6 : 0.35);
        const lineWidth = completed ? 3 : 2.5;

        // Y inverted: tier 0 at bottom, so prereq (lower tier) has higher Y
        const startY = from.y - halfH;
        const endY = to.y + halfH;
        const midY = (startY + endY) / 2;

        // Build bezier points
        const steps = 16;
        const points: { x: number; y: number }[] = [{ x: from.x, y: startY }];
        for (let s = 1; s <= steps; s++) {
          const t = s / steps;
          const mt = 1 - t;
          const bx = mt * mt * mt * from.x + 3 * mt * mt * t * from.x
            + 3 * mt * t * t * to.x + t * t * t * to.x;
          const by = mt * mt * mt * startY + 3 * mt * mt * t * midY
            + 3 * mt * t * t * midY + t * t * t * endY;
          points.push({ x: bx, y: by });
        }

        // Draw soft glow behind (wider, transparent)
        gfxGlow.lineStyle(lineWidth + 4, lineColor, lineAlpha * 0.25);
        gfxGlow.beginPath();
        gfxGlow.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) gfxGlow.lineTo(points[i].x, points[i].y);
        gfxGlow.strokePath();

        // Draw main line
        gfx.lineStyle(lineWidth, lineColor, lineAlpha);
        gfx.beginPath();
        gfx.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) gfx.lineTo(points[i].x, points[i].y);
        gfx.strokePath();

        // Small dots at connection endpoints for clarity
        gfx.fillStyle(lineColor, lineAlpha);
        gfx.fillCircle(from.x, startY, 3);
        gfx.fillCircle(to.x, endY, 3);
      }
    }
  }

  // ── Node selection ──

  private selectNode(node: LevelNode): void {
    this.selectedNode = node;
    this.cinematicWatcher?.fire('levelSelected');

    // Move selection glow to this node
    const { x, y } = this.nodeScreenPos(node);
    this.selectionGlow.clear();
    this.selectionGlow.setVisible(true);
    this.selectionGlow.setPosition(x, y);
    // Outer glow
    this.selectionGlow.lineStyle(3, 0xffcc00, 0.8);
    this.selectionGlow.strokeRect(
      -NODE_RECT_W / 2 - 4, -NODE_RECT_H / 2 - 4,
      NODE_RECT_W + 8, NODE_RECT_H + 8,
    );
    // Inner bright edge
    this.selectionGlow.lineStyle(1, 0xffcc00, 0.4);
    this.selectionGlow.strokeRect(
      -NODE_RECT_W / 2 - 1, -NODE_RECT_H / 2 - 1,
      NODE_RECT_W + 2, NODE_RECT_H + 2,
    );

    // Update info panel
    this.infoPanel.removeAll(true);

    const unlocked = ProgressionManager.isUnlocked(node.id);
    const completed = ProgressionManager.isCompleted(node.id);
    const canUnlock = ProgressionManager.canUnlock(node.id);

    const customTag = CampaignOverrideStore.has(node.id) ? ' [CUSTOM]' : '';
    const lines: string[] = [
      `${node.name}${customTag}`,
      `Difficulty: ${node.difficulty}`,
      `Size: ${node.mapParams.cols}x${node.mapParams.rows}`,
      `Paths: ${node.mapParams.pathCount}  |  Waves: ${node.waves}`,
      `Layout: ${node.mapParams.layoutType}`,
      ``,
      `Enemies: ${node.allowedEnemies.length} types`,
    ];

    const demoCapped = isDemoCapped(node) && !isDevMode();
    if (demoCapped) {
      lines.push('', '[DEMO VERSION]');
      lines.push('Available in the full game!');
    } else if (!unlocked && !canUnlock) {
      lines.push('', '[LOCKED]');
      if (node.prereqs.length > 0) {
        lines.push(`Requires: complete a previous level`);
      }
      lines.push(`Cost: $${node.unlockCost}`);
    }

    if (completed) {
      const stars = ProgressionManager.getStars(node.id);
      lines.push('', `Completed! ${'★'.repeat(stars)}${'☆'.repeat(3 - stars)}`);
    }

    const text = this.add.text(0, 0, lines.join('\n'), {
      fontSize: '12px', color: '#cccccc', fontFamily: 'monospace',
      lineSpacing: 4, wordWrap: { width: 360 },
    });
    this.infoPanel.add(text);

    // Re-ignore info panel children on treeCam (new objects were added dynamically)
    this.treeCam.ignore(this.infoPanel);

    // Update action button (hidden for demo-capped levels)
    if (demoCapped) {
      this.actionBtn.setVisible(false);
    } else if (canUnlock) {
      this.actionLabel.setText(`UNLOCK $${node.unlockCost}`);
      const gfx = (this as any)._actionGfx as Phaser.GameObjects.Graphics;
      gfx.clear();
      fillTechRect(gfx, -100, -20, 200, 40, 0x335588, 0.3);
      drawTechRect(gfx, -100, -20, 200, 40, 0x335588, 0.8, 1.5);
      this.actionBtn.setVisible(true);
    } else if (unlocked) {
      this.actionLabel.setText(t('levelTree.deploy'));
      const gfx = (this as any)._actionGfx as Phaser.GameObjects.Graphics;
      gfx.clear();
      fillTechRect(gfx, -100, -20, 200, 40, TECH.SUCCESS, 0.3);
      drawTechRect(gfx, -100, -20, 200, 40, TECH.SUCCESS, 0.8, 1.5);
      this.actionBtn.setVisible(true);
    } else {
      this.actionBtn.setVisible(false);
    }

    // Dev mode: show EDIT MAP button for unlocked nodes
    this.editMapBtn.setVisible(isDevMode() && unlocked);
    this.cineEditorBtn.setVisible(isDevMode());

    // Render map preview only for completed nodes
    if (completed) {
      this.renderPreview(node);
    } else {
      this.renderLockedPlaceholder();
    }
  }

  private renderLockedPlaceholder(): void {
    this.previewContainer.removeAll(true);
    // Dark placeholder with lock icon
    const bg = this.add.rectangle(185, 110, 370, 220, 0x111122, 0.8);
    bg.setStrokeStyle(2, 0x333366);
    this.previewContainer.add(bg);
    const lockIcon = this.add.image(185, 90, 'icon_blocked')
      .setDisplaySize(38, 38).setOrigin(0.5);
    this.previewContainer.add(lockIcon);
    const lockText = this.add.text(185, 120, t('levelTree.locked'), {
      fontSize: '14px', color: '#666688', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5);
    this.previewContainer.add(lockText);
    // Re-ignore on treeCam
    this.treeCam.ignore(this.previewContainer);
  }

  // ── Map preview ──

  private renderPreview(node: LevelNode): void {
    this.previewContainer.removeAll(true);

    // Generate the map to show preview
    const map = generateMap(
      node.id, node.name, node.mapSeed,
      node.mapParams, node.difficulty, node.allowedEnemies,
    );

    const maxW = 370;
    const maxH = 220;
    const scale = Math.min(maxW / (map.cols * TILE_SIZE), maxH / (map.rows * TILE_SIZE), 1);
    const tileS = TILE_SIZE * scale;

    const COLORS: Record<number, number> = {
      [TileType.GROUND]: 0x2a2a2a,
      [TileType.WALL]: 0x444444,
      [TileType.PATH]: 0x886633,
      [TileType.PLACEMENT]: 0x335533,
      [TileType.BROKEN_PLACEMENT]: 0x3a2a2a,
      [TileType.BASE]: 0x2244cc,
      [TileType.SPAWN]: 0xcc2222,
    };

    for (let row = 0; row < map.rows; row++) {
      for (let col = 0; col < map.cols; col++) {
        const tile = map.grid[row][col];
        const color = COLORS[tile] ?? 0x1a1a1a;
        const x = col * tileS + tileS / 2;
        const y = row * tileS + tileS / 2;
        const rect = this.add.rectangle(x, y, tileS - 1, tileS - 1, color);
        this.previewContainer.add(rect);

        if (tile === TileType.SPAWN) {
          const t = this.add.text(x, y, 'S', {
            fontSize: `${Math.max(6, tileS * 0.4)}px`, color: '#ffffff',
            fontFamily: 'monospace', fontStyle: 'bold',
          }).setOrigin(0.5);
          this.previewContainer.add(t);
        }
        if (tile === TileType.BASE) {
          const t = this.add.text(x, y, 'B', {
            fontSize: `${Math.max(6, tileS * 0.4)}px`, color: '#ffffff',
            fontFamily: 'monospace', fontStyle: 'bold',
          }).setOrigin(0.5);
          this.previewContainer.add(t);
        }
      }
    }

    // Draw paths
    for (const path of map.paths) {
      for (let i = 0; i < path.length - 1; i++) {
        const from = path[i];
        const to = path[i + 1];
        const line = this.add.line(
          0, 0,
          from.col * tileS + tileS / 2, from.row * tileS + tileS / 2,
          to.col * tileS + tileS / 2, to.row * tileS + tileS / 2,
          0xffaa00, 0.6,
        ).setLineWidth(1.5).setOrigin(0, 0);
        this.previewContainer.add(line);
      }
    }

    // Re-ignore preview children on treeCam (new objects were added dynamically)
    this.treeCam.ignore(this.previewContainer);
  }

  // ── Action button ──

  private onAction(): void {
    if (!this.selectedNode) return;
    const node = this.selectedNode;

    if (ProgressionManager.canUnlock(node.id)) {
      if (ProgressionManager.tryUnlock(node.id)) {
        // levelUnlock SFX auto-triggered via EventBus
        this.playUnlockAnimation(node);
      }
      return;
    }

    if (ProgressionManager.isUnlocked(node.id)) {
      SoundManager.play('clickConfirm');
      this.startLevel(node);
    }
  }

  /** Animate unlock: button shatters, node flashes, then scene refreshes */
  private playUnlockAnimation(node: LevelNode): void {
    this.actionBtn.setVisible(false);
    const nodeContainer = this.nodeContainers.get(node.id);

    // 1. Button shatter — fragments fly outward
    const bx = 190, by = CANVAS_HEIGHT - 80;
    for (let i = 0; i < 10; i++) {
      const angle = (i / 10) * Math.PI * 2 + Math.random() * 0.5;
      const dist = 40 + Math.random() * 60;
      const frag = this.add.rectangle(
        bx + (Math.random() - 0.5) * 40, by + (Math.random() - 0.5) * 12,
        8 + Math.random() * 14, 3 + Math.random() * 6, 0x335588, 0.9,
      ).setDepth(250).setAngle(Math.random() * 360);
      this.tweens.add({
        targets: frag,
        x: bx + Math.cos(angle) * dist,
        y: by + Math.sin(angle) * dist + 25,
        angle: frag.angle + (Math.random() - 0.5) * 360,
        alpha: 0, scaleX: 0.2, scaleY: 0.2,
        duration: 450 + Math.random() * 300,
        ease: 'Quad.easeOut',
        onComplete: () => frag.destroy(),
      });
    }

    // 2. Camera shake
    this.cameras.main.shake(200, 0.006);

    // 3. Flash + sparkles on the tree node
    if (nodeContainer) {
      this.time.delayedCall(200, () => {
        const flash = this.add.rectangle(0, 0, NODE_RECT_W + 10, NODE_RECT_H + 10, 0xffffff, 0.8);
        nodeContainer.add(flash);
        this.tweens.add({
          targets: flash, alpha: 0, duration: 400, ease: 'Quad.easeOut',
          onComplete: () => {
            flash.destroy();
            // Gold sparkle burst
            for (let j = 0; j < 14; j++) {
              const a = Math.random() * Math.PI * 2;
              const r = 20 + Math.random() * 30;
              const spark = this.add.circle(
                Math.cos(a) * 4, Math.sin(a) * 4,
                1.5 + Math.random() * 2, 0xffcc00, 1,
              );
              nodeContainer.add(spark);
              this.tweens.add({
                targets: spark,
                x: Math.cos(a) * r, y: Math.sin(a) * r,
                alpha: 0, duration: 400, ease: 'Quad.easeOut',
                onComplete: () => spark.destroy(),
              });
            }
          },
        });
      });
    }

    // 4. After animation completes, restart scene to refresh all node visuals
    this.time.delayedCall(850, () => {
      this.scene.restart({ selectNodeId: node.id });
    });
  }

  private onEditMap(): void {
    if (!this.selectedNode) return;
    const node = this.selectedNode;
    // Use existing override if available, otherwise generate from seed
    const override = CampaignOverrideStore.get(node.id);
    const map = override ?? generateMap(
      node.id, node.name, node.mapSeed,
      node.mapParams, node.difficulty, node.allowedEnemies,
    );
    SoundManager.play('click');
    this.scene.start('MapEditorScene', {
      mapDef: map,
      campaignLevelId: node.id,
      hasOverride: !!override,
    });
  }

  private startLevel(node: LevelNode): void {
    // Use override if available, otherwise generate from seed
    const override = CampaignOverrideStore.get(node.id);
    const map = override ?? generateMap(
      node.id, node.name, node.mapSeed,
      node.mapParams, node.difficulty, node.allowedEnemies,
    );
    // Inject cinematic triggers for first campaign level (tutorial replacement)
    if (node.tier === 0 && !map.cinematics?.length) {
      map.cinematics = [
        { sequenceId: 'demo-wave-1', event: 'mapStart' as const, once: true },
      ];
    }

    setActiveMap(map);

    // Store level node info in registry for game-over flow
    this.registry.set('currentLevelNode', node);
    this.registry.set('startingResources', startingResources(node.tier));

    // Stop and start game
    if (this.scene.isActive('UIScene')) this.scene.stop('UIScene');
    this.scene.start('GameScene');
    this.scene.launch('UIScene');
    this.scene.stop('LevelTreeScene');
  }

  /**
   * Get medal tier for a completed node.
   * Placeholder: based on stars for now — will be replaced with proper evaluation later.
   */
  private getMedalTier(node: LevelNode): MedalTier {
    const rankTier = ProgressionManager.getRankTier(node.id);
    if (rankTier >= 4) return MedalTier.PLATINUM;
    const stars = ProgressionManager.getStars(node.id);
    if (stars >= 3) return MedalTier.GOLD;
    if (stars >= 2) return MedalTier.SILVER;
    if (stars >= 1) return MedalTier.BRONZE;
    return MedalTier.IRON;
  }

  private buildProgressDisplay(): void {
    const completed = LEVEL_TREE.filter(n => ProgressionManager.isCompleted(n.id)).length;
    const total = LEVEL_TREE.length;
    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

    const cx = 15;
    const cy = 50;
    const barW = 200;
    const barH = 14;

    this.progressContainer = this.add.container(0, 0).setDepth(200);

    // Label
    const label = this.add.text(cx, cy, `PROGRESS  ${completed}/${total}`, {
      fontSize: '9px', color: '#8899aa', fontFamily: 'monospace',
    });
    this.progressContainer.add(label);

    // Bar background
    const barGfx = this.add.graphics();
    barGfx.fillStyle(0x111122, 0.9);
    barGfx.fillRoundedRect(cx, cy + 14, barW, barH, 3);
    barGfx.lineStyle(1, 0x334466, 0.8);
    barGfx.strokeRoundedRect(cx, cy + 14, barW, barH, 3);
    this.progressContainer.add(barGfx);

    // Filled portion — gradient from amber to gold
    if (pct > 0) {
      const fillW = Math.max(4, (barW - 4) * (pct / 100));
      const fillGfx = this.add.graphics();
      fillGfx.fillStyle(0xcc8800, 0.9);
      fillGfx.fillRoundedRect(cx + 2, cy + 16, fillW, barH - 4, 2);
      // Bright highlight on top half
      fillGfx.fillStyle(0xffcc00, 0.4);
      fillGfx.fillRect(cx + 2, cy + 16, fillW, (barH - 4) / 2);
      this.progressContainer.add(fillGfx);
    }

    // Percentage text (centered on bar)
    const pctText = this.add.text(cx + barW / 2, cy + 14 + barH / 2, `${pct}%`, {
      fontSize: '10px', color: '#ffffff', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5);
    this.progressContainer.add(pctText);
  }

  private updateCurrencyDisplay(): void {
    const gap = 10; // gap between groups
    const icoR = 14; // icon half-size (icoSz/2)
    // Coin amount
    this.currencyText.setText(`${ProgressionManager.getCurrency()}`);
    let x = this.currencyText.x + this.currencyText.width + gap;
    // Co icon + count
    this.resCoIcon.setX(x + icoR);
    x += icoR * 2 + 4;
    this.resourceText.setText(`${ResourceManager.co}`).setX(x);
    x += this.resourceText.width + gap;
    // Electro icon + count
    this.resElectroIcon.setX(x + icoR);
    x += icoR * 2 + 4;
    this.electroCountText!.setText(`${ResourceManager.electro}`).setX(x);
  }

  shutdown(): void {
    if (this.treeCam) {
      this.cameras.remove(this.treeCam);
      this.treeCam = undefined as any;
    }
  }
}
